import { ArrowLeft, ArrowRightLeft, Gift, Sparkles, TrendingDown } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { allowsSubscriptionChange, formatBR, formatDayBR } from '@control-center/shared';

import { PillButton } from '../../components/clients/PillButton';
import { BillingDayField } from '../../components/subscriptions/BillingDayField';
import { DiscountDisplay } from '../../components/subscriptions/DiscountDisplay';
import { SkipBillingButton } from '../../components/subscriptions/SkipBillingButton';
import { CourtesyUpgradeModal } from '../../components/subscriptions/CourtesyUpgradeModal';
import { DowngradeModal } from '../../components/subscriptions/DowngradeModal';
import { EntitlementsList } from '../../components/subscriptions/EntitlementsList';
import { PendingCancellationBanner } from '../../components/subscriptions/PendingCancellationBanner';
import { PendingDowngradeBanner } from '../../components/subscriptions/PendingDowngradeBanner';
import { SubscriptionActions } from '../../components/subscriptions/SubscriptionActions';
import { TransferModal } from '../../components/subscriptions/TransferModal';
import { TrialBanner } from '../../components/subscriptions/TrialBanner';
import { UpgradeModal } from '../../components/subscriptions/UpgradeModal';
import { useSubscription } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import { useAuthStore } from '../../stores/useAuthStore';

// Mudar plano, dono ou preço: a regra é a MESMA que o back aplica, importada de
// `@control-center/shared`. Antes esta tela mantinha uma whitelist própria, que
// já divergia do back em `pending_provisioning` — botão escondido pra operação
// que a API aceitava. No sentido contrário a divergência é pior: botão que o
// operador clica e leva 409 na cara.

function formatBRL(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return 'R$ —';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const STATUS_LABEL: Record<string, string> = {
  ativa: 'Ativa',
  em_teste: 'Em teste',
  trial_expired: 'Trial expirado',
  pausada: 'Pausada',
  pending_cancellation: 'Cancelamento pendente',
  cancelada: 'Cancelada',
  inadimplente: 'Inadimplente',
};

export function SubscriptionDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const q = useSubscription(id);
  const role = useAuthStore((s) => s.user?.role);
  const canEditBillingDay = role === 'admin' || role === 'financeiro';
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [courtesyOpen, setCourtesyOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  if (q.isLoading) {
    return <div className="text-[13px] text-muted">Carregando…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-[14px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
        {extractErrorMessage(q.error, 'Assinatura não encontrada.')}
      </div>
    );
  }

  const sub = q.data;
  const podeMudar = allowsSubscriptionChange(sub.status);
  const canUpgrade = Boolean(sub.plan) && podeMudar;
  const hasPendingDowngrade = sub.pendingPlanChange !== null && sub.pendingPlanChange !== undefined;
  // Cortesia é mais estreita que upgrade de propósito: dar plano melhor de graça
  // exige assinatura saudável (o back aceita só `ativa`/`em_teste`). Repetir a
  // condição aqui manteria a divergência que este arquivo acabou de matar — a
  // tela pergunta "está pagando em dia?", não "não está bloqueada?".
  const canCourtesy =
    Boolean(sub.plan) &&
    (sub.status === 'ativa' || sub.status === 'em_teste') &&
    !hasPendingDowngrade &&
    Boolean(sub.nextBillingDate);
  const canDowngrade = Boolean(sub.plan) && podeMudar && !hasPendingDowngrade;
  const canTransfer = podeMudar && sub.origin !== 'saas_direct' && !hasPendingDowngrade;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/subscriptions')}
            aria-label="Voltar"
            className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-line text-muted transition-colors hover:bg-panel-2"
          >
            <ArrowLeft size={14} strokeWidth={1.8} />
          </button>
          <div>
            <h1 className="m-0 text-[22px] font-bold tracking-[-0.025em] text-ink">
              {sub.client.name}
            </h1>
            <div className="text-[12px] text-muted">
              {sub.plan?.name ?? 'Sem plano'} · {STATUS_LABEL[sub.status] ?? sub.status}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canUpgrade && (
            <PillButton
              type="button"
              onClick={() => setUpgradeOpen(true)}
              icon={<Sparkles size={12} strokeWidth={2} />}
            >
              Upgrade
            </PillButton>
          )}
          {canCourtesy && (
            <PillButton
              type="button"
              onClick={() => setCourtesyOpen(true)}
              icon={<Gift size={12} strokeWidth={2} />}
            >
              Cortesia
            </PillButton>
          )}
          {canDowngrade && (
            <PillButton
              type="button"
              onClick={() => setDowngradeOpen(true)}
              icon={<TrendingDown size={12} strokeWidth={2} />}
            >
              Downgrade
            </PillButton>
          )}
          {canTransfer && (
            <PillButton
              type="button"
              onClick={() => setTransferOpen(true)}
              icon={<ArrowRightLeft size={12} strokeWidth={2} />}
            >
              Transferir
            </PillButton>
          )}
          <SubscriptionActions subscriptionId={sub.id} status={sub.status} />
        </div>
      </header>

      {hasPendingDowngrade && sub.pendingPlanChange && (
        <PendingDowngradeBanner
          subscriptionId={sub.id}
          scheduledFor={sub.pendingPlanChange.scheduledFor}
          targetPlanLabel={sub.pendingPlanChange.targetPlanId}
          changeType={sub.pendingPlanChange.changeType}
        />
      )}

      <UpgradeModal subscription={sub} open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      <CourtesyUpgradeModal subscription={sub} open={courtesyOpen} onOpenChange={setCourtesyOpen} />
      <DowngradeModal subscription={sub} open={downgradeOpen} onOpenChange={setDowngradeOpen} />
      <TransferModal subscription={sub} open={transferOpen} onOpenChange={setTransferOpen} />

      {/* Story 3.9 — Trial banner (em_teste ou trial_expired) */}
      <TrialBanner subscription={sub} />

      {/* Story 3.8 — Desconto manual (inline) */}
      <DiscountDisplay subscription={sub} />

      {/* Cortesia: pular uma cobrança (afiliados 2026-08-25) */}
      <div className="flex justify-start">
        <SkipBillingButton
          subscriptionId={sub.id}
          status={sub.status}
          nextBillingDate={sub.nextBillingDate}
          billingDay={sub.billingDay}
        />
      </div>

      {sub.status === 'pending_cancellation' && (
        <PendingCancellationBanner subscriptionId={sub.id} pendingCancellationAt={sub.endDate} />
      )}

      <section className="rounded-[14px] border border-line bg-panel p-5">
        <h3 className="m-0 mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Dados
        </h3>
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Item label="Preço" value={`${formatBRL(sub.price)}`} />
          <Item label="Início" value={formatDayBR(sub.startDate)} />
          <Item label="Próxima cobrança" value={formatDayBR(sub.nextBillingDate)} />
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-3">Dia de cobrança</dt>
            <dd className="mt-1">
              <BillingDayField
                subscriptionId={sub.id}
                currentBillingDay={sub.billingDay}
                canEdit={canEditBillingDay}
              />
            </dd>
          </div>
          {sub.trialEndsAt && <Item label="Trial termina em" value={formatBR(sub.trialEndsAt)} />}
          <Item label="Auto-renew" value={sub.autoRenew ? 'Sim' : 'Não'} />
        </dl>
      </section>

      <EntitlementsList entitlements={sub.entitlements} />

      <div className="text-center text-[11px] text-muted-3">
        Lifecycle, upgrade/downgrade, transfer e billing day rolling chegam nas stories 3.3-3.10.
      </div>

      <PillButton type="button" onClick={() => navigate('/subscriptions')}>
        Voltar pra lista
      </PillButton>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-3">{label}</dt>
      <dd className="mt-1 text-[13px] text-ink">{value}</dd>
    </div>
  );
}
